import React, { useState } from 'react';
import type { ReviewDecision, ReviewItem, ScanResponse } from '../../lib/adminReviewApi';
import MotionClaimReviewPanel from './MotionClaimReviewPanel';
import MotionProjectionDryRunPanel from './MotionProjectionDryRunPanel';
import MotionReviewNotesPreviewPanel from './MotionReviewNotesPreviewPanel';
import MotionReviewNotesInsertionLedgerPanel from './MotionReviewNotesInsertionLedgerPanel';
import {
  AutoGateStatusBadge,
  DeliveryStatusBadge,
  ManualReviewStatusBadge,
  ScanStatusBadge,
  WorkflowStatusBadge,
} from './AdminReviewStatusBadge';

interface AdminReviewDetailPanelProps {
  item: ReviewItem | null;
  loading?: boolean;
  actionLoading?: boolean;
  lastScanResult?: ScanResponse | null;
  lastEvaluateSummary?: Record<string, unknown> | null;
  onRunEvaluate: () => void;
  onRunScan: () => void;
  onClaim: (reviewer: string) => void;
  onOpenDecision: (decision: ReviewDecision) => void;
  onMarkDelivered: () => void;
  onRequestFeedback: () => void;
}

function formatTimestamp(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

const InternalLabel: React.FC = () => (
  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
    Internal only
  </span>
);

const AdminReviewDetailPanel: React.FC<AdminReviewDetailPanelProps> = ({
  item,
  loading,
  actionLoading,
  lastScanResult,
  lastEvaluateSummary,
  onRunEvaluate,
  onRunScan,
  onClaim,
  onOpenDecision,
  onMarkDelivered,
  onRequestFeedback,
}) => {
  const [claimReviewer, setClaimReviewer] = useState('');

  if (loading) {
    return (
      <div className="card sticky top-4 text-sm text-secondary-600 dark:text-secondary-300">
        Loading review item…
      </div>
    );
  }

  if (!item) {
    return (
      <div className="card sticky top-4 text-sm text-secondary-600 dark:text-secondary-300">
        Select a review item to inspect workflow state, scans, and actions.
      </div>
    );
  }

  const actionDisabled = actionLoading;

  return (
    <div className="card sticky top-4 space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-secondary-900 dark:text-white">Review Detail</h2>
        <p className="mt-1 font-mono text-xs text-secondary-500 dark:text-secondary-400">
          {item.upload_id}
        </p>
      </div>

      <section className="space-y-2 text-sm">
        <h3 className="font-medium text-secondary-900 dark:text-white">Report identity</h3>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
          <dt className="text-secondary-500">Opponent gamertag</dt>
          <dd>{item.opponent_gamertag || '—'}</dd>
          <dt className="text-secondary-500">Opponent team</dt>
          <dd>{item.opponent_team || '—'}</dd>
          <dt className="text-secondary-500">Owner team</dt>
          <dd>{item.owner_team || '—'}</dd>
          <dt className="text-secondary-500">League</dt>
          <dd>{item.league || '—'}</dd>
          <dt className="text-secondary-500">Report type</dt>
          <dd>{item.report_type || '—'}</dd>
          <dt className="text-secondary-500">Analysis quality</dt>
          <dd>{item.analysis_quality || '—'}</dd>
        </dl>
      </section>

      <section className="space-y-2 text-sm">
        <h3 className="font-medium text-secondary-900 dark:text-white">Workflow state</h3>
        <div className="flex flex-wrap gap-2">
          <WorkflowStatusBadge status={item.workflow_status} />
          <DeliveryStatusBadge status={item.delivery_status} />
          <ManualReviewStatusBadge status={item.manual_review_status} />
          <AutoGateStatusBadge status={item.auto_gate_status} />
        </div>
        <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
          <dt className="text-secondary-500">Reviewer</dt>
          <dd>{item.reviewer || '—'}</dd>
          <dt className="text-secondary-500">Created</dt>
          <dd>{formatTimestamp(item.created_at)}</dd>
          <dt className="text-secondary-500">Updated</dt>
          <dd>{formatTimestamp(item.updated_at)}</dd>
        </dl>
      </section>

      <section className="space-y-2 text-sm">
        <h3 className="font-medium text-secondary-900 dark:text-white">Scan summary</h3>
        <div className="flex flex-wrap gap-2">
          <ScanStatusBadge
            status={item.projection_scan_status}
            count={item.projection_scan_hits_count}
          />
          <ScanStatusBadge
            status={item.forbidden_scan_status}
            count={item.forbidden_scan_hits_count}
          />
        </div>
        {lastScanResult && (
          <p className="text-xs text-secondary-500 dark:text-secondary-400">
            Last scan: projection {lastScanResult.projection_scan?.status || '—'} (
            {lastScanResult.projection_scan?.hits_count ?? 0} hits), forbidden{' '}
            {lastScanResult.forbidden_scan?.status || '—'} (
            {lastScanResult.forbidden_scan?.hits_count ?? 0} hits)
          </p>
        )}
      </section>

      {(item.auto_gate_reasons?.length || item.risk_flags?.length || item.hold_reason) && (
        <section className="space-y-2 text-sm">
          <h3 className="font-medium text-secondary-900 dark:text-white">
            Auto-gate & risk
            <InternalLabel />
          </h3>
          {item.auto_gate_reasons?.length ? (
            <ul className="list-disc pl-5 text-secondary-700 dark:text-secondary-300">
              {item.auto_gate_reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          ) : null}
          {item.risk_flags?.length ? (
            <p className="text-secondary-700 dark:text-secondary-300">
              Risk flags: {item.risk_flags.join(', ')}
            </p>
          ) : null}
          {item.hold_reason ? (
            <p className="text-secondary-700 dark:text-secondary-300">
              Hold reason: {item.hold_reason}
            </p>
          ) : null}
          {item.failure_class ? (
            <p className="text-secondary-700 dark:text-secondary-300">
              Failure class: {item.failure_class}
            </p>
          ) : null}
        </section>
      )}

      <section className="space-y-2 text-sm">
        <h3 className="font-medium text-secondary-900 dark:text-white">
          Notes & summary
          <InternalLabel />
        </h3>
        <p className="rounded-lg bg-secondary-50 p-3 text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200">
          <span className="block text-xs uppercase text-secondary-500">Customer-safe summary</span>
          {item.customer_safe_summary || '—'}
        </p>
        <p className="rounded-lg bg-amber-50 p-3 text-secondary-800 dark:bg-amber-950/30 dark:text-amber-100">
          <span className="block text-xs uppercase text-amber-800 dark:text-amber-300">
            Internal notes — do not send to customer
          </span>
          {item.review_notes_internal || '—'}
        </p>
      </section>

      {(item.projected_payload_url ||
        item.full_results_uri_internal ||
        item.analysis_diagnostics_uri_internal) && (
        <section className="space-y-2 text-sm">
          <h3 className="font-medium text-secondary-900 dark:text-white">
            Source artifacts
            <InternalLabel />
          </h3>
          <ul className="space-y-1 break-all font-mono text-xs text-secondary-600 dark:text-secondary-300">
            {item.projected_payload_url ? <li>Projected: {item.projected_payload_url}</li> : null}
            {item.full_results_uri_internal ? (
              <li>Full results: {item.full_results_uri_internal}</li>
            ) : null}
            {item.analysis_diagnostics_uri_internal ? (
              <li>Diagnostics: {item.analysis_diagnostics_uri_internal}</li>
            ) : null}
          </ul>
        </section>
      )}

      <MotionClaimReviewPanel uploadId={item.upload_id} />

      <MotionProjectionDryRunPanel uploadId={item.upload_id} />

      <MotionReviewNotesPreviewPanel uploadId={item.upload_id} />

      <MotionReviewNotesInsertionLedgerPanel uploadId={item.upload_id} />

      {lastEvaluateSummary && (
        <section className="space-y-2 text-sm">
          <h3 className="font-medium text-secondary-900 dark:text-white">
            Last evaluate
            <InternalLabel />
          </h3>
          <pre className="max-h-40 overflow-auto rounded-lg bg-secondary-50 p-3 text-xs text-secondary-700 dark:bg-secondary-800 dark:text-secondary-200">
            {JSON.stringify(lastEvaluateSummary, null, 2)}
          </pre>
        </section>
      )}

      <section className="space-y-3 border-t border-secondary-200 pt-4 dark:border-secondary-700">
        <h3 className="font-medium text-secondary-900 dark:text-white">Actions</h3>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary text-sm disabled:opacity-50"
            disabled={actionDisabled}
            onClick={onRunEvaluate}
          >
            Run Evaluate
          </button>
          <button
            type="button"
            className="btn-secondary text-sm disabled:opacity-50"
            disabled={actionDisabled}
            onClick={onRunScan}
          >
            Run Scan
          </button>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[12rem] flex-1">
            <label className="mb-1 block text-xs text-secondary-500">Claim as reviewer</label>
            <input
              type="text"
              value={claimReviewer}
              onChange={(e) => setClaimReviewer(e.target.value)}
              placeholder="Reviewer name"
              className="input-field"
            />
          </div>
          <button
            type="button"
            className="btn-secondary text-sm disabled:opacity-50"
            disabled={actionDisabled || !claimReviewer.trim()}
            onClick={() => onClaim(claimReviewer.trim())}
          >
            Claim
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-primary text-sm disabled:opacity-50"
            disabled={actionDisabled}
            onClick={() => onOpenDecision('approve')}
          >
            Approve
          </button>
          <button
            type="button"
            className="btn-primary text-sm disabled:opacity-50"
            disabled={actionDisabled}
            onClick={() => onOpenDecision('approve_with_note')}
          >
            Approve With Note
          </button>
          <button
            type="button"
            className="btn-secondary text-sm disabled:opacity-50"
            disabled={actionDisabled}
            onClick={() => onOpenDecision('hold')}
          >
            Hold
          </button>
          <button
            type="button"
            className="btn-secondary text-sm disabled:opacity-50"
            disabled={actionDisabled}
            onClick={() => onOpenDecision('retry_required')}
          >
            Retry Required
          </button>
          <button
            type="button"
            className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            disabled={actionDisabled}
            onClick={() => onOpenDecision('fail')}
          >
            Fail
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary text-sm disabled:opacity-50"
            disabled={actionDisabled || item.delivery_status !== 'approved_internal'}
            onClick={onMarkDelivered}
          >
            Mark Delivered
          </button>
          <button
            type="button"
            className="btn-secondary text-sm disabled:opacity-50"
            disabled={actionDisabled || item.delivery_status !== 'delivered'}
            onClick={onRequestFeedback}
          >
            Request Feedback
          </button>
        </div>
      </section>
    </div>
  );
};

export default AdminReviewDetailPanel;
